import React, { useState } from "react";

import { AnimatePresence, motion } from "framer-motion";
import styled from "styled-components/macro";

import { ReactComponent as IconPlus } from "../../../assets/icons/icon_plus.svg";

type AnimatedItem = {
  img: string;
  number: string;
  title: string;
  title_color: string;
  description: string;
  title_strong: string;
  answers: string[];
};

type Props = {
  items: AnimatedItem[];
};

import {
  Circle,
  Icon,
  ListItem,
  Number,
  Text,
  Title,
} from "./styles"

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  border-top: 1px solid #dcdcdc;
`;

const BlockItem = styled.div`
  border-bottom: 1px solid #dcdcdc;
`;

const Header = styled.button`
  display: flex;
  justify-content: flex-start;
  align-items: center;
  padding: 22px 0;
  cursor: pointer;
  width: 100%;
`;

const TitleMobile = styled(Title)`
  font-size: 30px;
  margin: 0 0 0 10px;
  line-height: 86%;
`;

const ToggleContainer = styled.div`
  padding: 5px;
  display: flex;
  align-items: center;
  margin-left: auto;
`

const ToggleBase = styled.div<{ active: boolean }>`
  min-width: 14px;
  min-height: 14px;
  transform: ${({ active }) => (active ? "rotate(45deg)" : "rotate(0deg)")};
  transition: 0.2s;

  svg {
    display: block;
  }

  svg path {
    stroke: #A7A7A7;
  }
`;

const Content = styled(motion.div)`
  overflow: hidden;
`;

const Inner = styled.div`
  padding-bottom: 16px;
`;

const Description = styled.p`
  font-size: 15px;
  margin-bottom: 40px;
`;

const Strong = styled.div`
  font-weight: 500;
  font-size: 15px;
  margin-bottom: 26px;
`;

const List = styled.ul`
  margin-bottom: 12px;
`;

const TextMobile = styled(Text)`
  font-size: 15px;
`

const Image = styled.img`
  width: 100%;
  height: 360px;
  border-radius: 12px;
  margin-top: 20px;
  object-fit: cover;
`;

const Toggle: React.FC<{ active: boolean }> = ({ active }) => (
  <ToggleBase active={active}>
    <IconPlus />
  </ToggleBase>
);

export const AnimatedBlockMobile: React.FC<Props> = ({ items }) => {
  const [activeIndex, setActiveIndex] = useState<number | null>(0);

  const toggle = (index: number) => {
    setActiveIndex(prev => (prev === index ? null : index));
  };

  return (
    <Wrapper>
      {items.map((item, index) => (
        <BlockItem key={item.number}>
          <Header onClick={() => toggle(index)}>
            <Circle />
            <Number>{item.number}</Number>
            <TitleMobile>
              {item.title}
              <span> {item.title_color}</span>
            </TitleMobile>
            <ToggleContainer>
              <Toggle active={activeIndex === index} />
            </ToggleContainer>
          </Header>

          <AnimatePresence initial={false}>
            {activeIndex === index && (
              <Content
                initial="collapsed"
                animate="open"
                exit="collapsed"
                variants={{
                  open: { height: "auto", opacity: 1 },
                  collapsed: { height: 0, opacity: 0 },
                }}
                transition={{ duration: 0.3 }}
              >
                <Inner>
                  <Description>{item.description}</Description>
                  <Strong>{item.title_strong}</Strong>
                  <List>
                    {item.answers.map((answer, i) => (
                      <ListItem key={i}>
                        <Icon />
                        <TextMobile>{answer}</TextMobile>
                      </ListItem>
                    ))}
                  </List>
                  <Image src={item.img} alt="" />
                </Inner>
              </Content>
            )}
          </AnimatePresence>
        </BlockItem>
      ))}
    </Wrapper>
  );
};

